import type { ChessAction, ChessState, Promotion } from "./types";

const KNIGHT_OFFSETS = [
  [-1, -2], [1, -2], [-2, -1], [2, -1],
  [-1, 2], [1, 2], [-2, 1], [2, 1],
] as const;

const DIRECTIONS = [
  [-1, 0], [1, 0], [0, -1], [0, 1],
  [-1, -1], [1, 1], [1, -1], [-1, 1],
] as const;

export function decodeAction(label: number): ChessAction {
  if (!Number.isInteger(label) || label < 0 || label >= 4_672) {
    throw new RangeError(`Invalid PGX action label: ${label}`);
  }
  const from = Math.floor(label / 73);
  const plane = label % 73;
  const rank = from % 8;
  const file = Math.floor(from / 8);
  if (plane < 9) {
    const dc = plane % 3 === 0 ? 0 : plane % 3 === 1 ? 1 : -1;
    const toRank = rank + 1;
    const toFile = file + dc;
    return {
      from,
      to: toRank < 8 && toFile >= 0 && toFile < 8 ? toFile * 8 + toRank : -1,
      underpromotion: Math.floor(plane / 3) as Promotion,
    };
  }
  if (plane >= 65) {
    const offset = KNIGHT_OFFSETS[plane - 65]!;
    const toRank = rank + offset[0];
    const toFile = file + offset[1];
    return {
      from,
      to: toRank >= 0 && toRank < 8 && toFile >= 0 && toFile < 8
        ? toFile * 8 + toRank
        : -1,
      underpromotion: -1,
    };
  }
  const shifted = plane - 9;
  const direction = Math.floor(shifted / 7);
  const offset = shifted % 7;
  const distance = direction % 2 === 0 ? 7 - offset : offset + 1;
  const delta = DIRECTIONS[direction]!;
  const toRank = rank + delta[0] * distance;
  const toFile = file + delta[1] * distance;
  return {
    from,
    to: toRank >= 0 && toRank < 8 && toFile >= 0 && toFile < 8
      ? toFile * 8 + toRank
      : -1,
    underpromotion: -1,
  };
}

export function encodeAction(action: ChessAction): number {
  const { from, to, underpromotion } = action;
  const r0 = from % 8;
  const c0 = Math.floor(from / 8);
  const r1 = to % 8;
  const c1 = Math.floor(to / 8);
  const dr = r1 - r0;
  const dc = c1 - c0;
  if (underpromotion >= 0) {
    const variant = dc === 0 ? 0 : dc === 1 ? 1 : dc === -1 ? 2 : -1;
    if (dr !== 1 || variant < 0) throw new RangeError("Invalid underpromotion");
    return from * 73 + underpromotion * 3 + variant;
  }
  const knight = KNIGHT_OFFSETS.findIndex(([r, c]) => r === dr && c === dc);
  if (knight >= 0) return from * 73 + 65 + knight;
  const distance = Math.max(Math.abs(dr), Math.abs(dc));
  if (
    distance < 1 ||
    distance > 7 ||
    !((dr === 0) || (dc === 0) || Math.abs(dr) === Math.abs(dc))
  ) {
    throw new RangeError("Move cannot be represented by a PGX action plane");
  }
  const sr = Math.sign(dr);
  const sc = Math.sign(dc);
  const direction = DIRECTIONS.findIndex(([r, c]) => r === sr && c === sc);
  if (direction < 0) throw new RangeError("Invalid slider direction");
  const offset = direction % 2 === 0 ? 7 - distance : distance - 1;
  return from * 73 + 9 + direction * 7 + offset;
}

export function canonicalToAbsolute(square: number, color: 0 | 1): number {
  return color === 0 ? square : Math.floor(square / 8) * 8 + (7 - square % 8);
}

export function absoluteToCanonical(square: number, color: 0 | 1): number {
  return canonicalToAbsolute(square, color);
}

export function squareName(square: number): string {
  return `${String.fromCharCode(97 + Math.floor(square / 8))}${square % 8 + 1}`;
}

export function actionToUci(label: number, color: 0 | 1 = 0): string {
  const action = decodeAction(label);
  if (action.to < 0) throw new RangeError(`Off-board PGX action: ${label}`);
  const suffix = action.underpromotion < 0
    ? ""
    : action.underpromotion === 0 ? "r" : action.underpromotion === 1 ? "b" : "n";
  return `${squareName(canonicalToAbsolute(action.from, color))}${squareName(
    canonicalToAbsolute(action.to, color),
  )}${suffix}`;
}

export function uciToAction(state: ChessState, uci: string): number {
  const match = /^([a-h])([1-8])([a-h])([1-8])([qrbn])?$/.exec(uci.toLowerCase());
  if (!match) throw new RangeError(`Invalid UCI move: ${uci}`);
  const absolute = (file: string, rank: string) =>
    (file.charCodeAt(0) - 97) * 8 + Number(rank) - 1;
  const from = absoluteToCanonical(absolute(match[1]!, match[2]!), state.color);
  const to = absoluteToCanonical(absolute(match[3]!, match[4]!), state.color);
  const promotion = match[5];
  if (promotion === "q" || !promotion) {
    return encodeAction({ from, to, underpromotion: -1 });
  }
  const underpromotion = ({ r: 0, b: 1, n: 2 } as const)[promotion as "r" | "b" | "n"];
  return encodeAction({ from, to, underpromotion });
}
